import type { ChatSourceItem } from '../../sources';
import { PREDEFINED_SOURCES } from './constants';

const MAX_DNS_HOST_LENGTH = 253;

function isMatchedQuotePair(value: string): boolean {
  if (value.length < 2) return false;
  const first = value[0];
  if (first !== '"' && first !== "'") return false;
  return first === value[value.length - 1];
}

/**
 * Strip wrapping quotes and trailing prose punctuation from a URL source input.
 * Mirrors `shared/url/domain_normalization.py::strip_url_filter_wrapping`.
 */
function stripUrlFilterWrapping(input: string): string {
  let s = input.trim();
  while (isMatchedQuotePair(s)) {
    s = s.slice(1, -1).trim();
  }
  return s
    .replace(/^['"]+/, '')
    .replace(/[.,;]+$/, '')
    .replace(/['"]+$/, '')
    .trim();
}

/**
 * Normalize a URL source by removing protocol, www, and trailing slashes.
 * Preserves the path so that entries like "uptodate.com/contents" remain intact.
 */
export function normalizeDomain(input: string): string {
  return stripUrlFilterWrapping(input)
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/+$/, '');
}

/**
 * Check if a string looks like a valid domain or URL path
 */
function isPlausibleDnsHost(hostPart: string): boolean {
  if (!hostPart.includes('.')) return false;
  if (hostPart.startsWith('.')) return false;
  if (hostPart.endsWith('.')) return false;
  if (hostPart.includes('..')) return false;
  return hostPart.length <= MAX_DNS_HOST_LENGTH;
}

export function isValidDomain(input: string): boolean {
  const normalized = normalizeDomain(input);
  if (!normalized) return false;
  if (normalized.includes(' ') || /['"]/.test(normalized)) return false;
  const hostSlice = normalized.split(/[/?#]/, 1)[0] ?? '';
  return isPlausibleDnsHost(bareHost(hostSlice));
}

export type DomainInputStatus = 'valid' | 'invalid' | 'duplicate' | 'blocked';

export interface DomainInputClassification {
  /** Normalized form of the input (the value passed to `onAddDomain`). */
  domain: string;
  status: DomainInputStatus;
}

/** Shared empty default so the no-blocklist path never allocates a Set. */
const EMPTY_BLOCKED_HOSTS: ReadonlySet<string> = new Set();

/**
 * Classify a raw "Add source" query against the collection's existing
 * domains and (optionally) a list of known sources.
 *
 * Returns `null` when input is empty/whitespace; otherwise tags the input as
 * `valid`, `invalid`, `duplicate`, or `blocked`. Callers decide how to render
 * — see `CustomDomainRow`.
 *
 * EVI-1751 (product): gate Add source with the content-safety **blocklist**
 * only (RCO-56). Custom domains that are not denylisted are addable; the
 * curated clinical catalog is search/suggest UX, not an allowlist.
 *
 * The duplicate rule is intentionally stricter than current BE collection
 * normalization (treats `www.` and case variants as duplicates). Canonical
 * persistence is still whatever BE stores — this is a client-side UX guard.
 * Keep `normalizeDomain` in sync conceptually with
 * `utils/domain_normalization.py::normalize_url_filter` on the BE.
 *
 * Label-based duplicate detection: when `knownSources` is provided and the
 * user types a source's display label (e.g. "PubMed") rather than its domain,
 * we still classify as `duplicate` if the matching domain is excluded.
 *
 * `excludedDomains` accepts either an array or a pre-built `ReadonlySet`.
 * In React callers we already memoize a `Set` (`useFilteredSources.excludedSet`);
 * passing it through avoids re-allocating one per call on every keystroke.
 *
 * `knownSources[].labelLower` is required and is expected to be pre-computed
 * (typically by `useAllSources`) so this function does no per-call
 * lowercasing of the source list — it can be called on every keystroke.
 *
 * `blockedHosts` is a pre-normalized `Set` of bare hosts (build it once with
 * `buildBlockedHostSet`, typically memoized off `useBlockedDomains`). Passing a
 * Set rather than the raw list avoids re-normalizing the denylist on every
 * keystroke.
 */
export function classifyDomainInput(
  rawInput: string,
  excludedDomains: ReadonlySet<string> | readonly string[],
  knownSources: { domain: string; labelLower: string }[] = [],
  blockedHosts: ReadonlySet<string> = EMPTY_BLOCKED_HOSTS
): DomainInputClassification | null {
  const trimmed = rawInput.trim();
  if (!trimmed) return null;

  const normalized = normalizeDomain(trimmed);
  const excludedSet =
    excludedDomains instanceof Set ? excludedDomains : new Set(excludedDomains);

  if (excludedSet.has(normalized)) {
    return { domain: normalized, status: 'duplicate' };
  }

  const lowerTrimmed = trimmed.toLowerCase();
  const labelMatch = knownSources.find((s) => s.labelLower === lowerTrimmed);
  if (labelMatch && excludedSet.has(labelMatch.domain)) {
    return { domain: labelMatch.domain, status: 'duplicate' };
  }

  // Content-safety denylist — never addable. The BE re-checks on write and is
  // the source of truth; this gives the user immediate inline feedback.
  if (isBlockedHost(trimmed, blockedHosts)) {
    return { domain: normalized, status: 'blocked' };
  }

  if (!isValidDomain(trimmed)) {
    return { domain: normalized, status: 'invalid' };
  }

  return { domain: normalized, status: 'valid' };
}

/**
 * Reduce any URL/host/path input to its bare host: lowercased, no scheme, no
 * `www.`, no path, no `user@` userinfo, no `:port`. Mirrors the backend
 * `extract_bare_host` + `host_in_denylist` stripping so FE/BE agree on matches.
 */
function bareHost(input: string): string {
  let host = normalizeDomain(input); // drops scheme, www., trailing slash; lowercases
  const slash = host.indexOf('/');
  if (slash !== -1) host = host.slice(0, slash); // drop path
  const at = host.lastIndexOf('@');
  if (at !== -1) host = host.slice(at + 1); // drop userinfo
  const colon = host.indexOf(':');
  if (colon !== -1) host = host.slice(0, colon); // drop port
  return host;
}

/**
 * Normalize a content-safety blocklist into a `Set` of bare hosts. The denylist
 * (from `GET /chat/blocked-domains`) is near-static, so callers build this once
 * (memoized) and reuse it across keystrokes rather than re-normalizing per call.
 */
export function buildBlockedHostSet(blocklist: Iterable<string>): ReadonlySet<string> {
  const hosts = new Set<string>();
  for (const entry of blocklist) {
    const host = bareHost(entry);
    if (host) hosts.add(host);
  }
  return hosts;
}

/**
 * True if `input` resolves to a host that is, or is a subdomain of, any host in
 * `blockedHosts` (a pre-normalized `Set` from `buildBlockedHostSet`).
 *
 * The server is the source of truth (it re-checks on write); this powers inline
 * UX so a clinician gets immediate feedback instead of a raw 400. Matching walks
 * the input's parent suffixes against the Set — O(labels) per call rather than
 * O(blocklist) — so `videos.badsite.com` is caught by a `badsite.com` entry but
 * a sibling like `notbadsite.com` is not.
 */
export function isBlockedHost(input: string, blockedHosts: ReadonlySet<string>): boolean {
  return hostInSet(input, blockedHosts);
}

/** True if input host (or a parent suffix) is in `hosts`. */
function hostInSet(input: string, hosts: ReadonlySet<string>): boolean {
  if (hosts.size === 0) return false;
  let candidate = bareHost(input);
  while (candidate) {
    if (hosts.has(candidate)) return true;
    const dot = candidate.indexOf('.');
    if (dot === -1) break;
    candidate = candidate.slice(dot + 1);
  }
  return false;
}

/**
 * Convenience matcher that builds the host set then checks membership. Prefer
 * `buildBlockedHostSet` + `isBlockedHost` in hot paths (e.g. per-keystroke
 * filtering); this is for one-off checks and tests.
 */
export function isBlockedDomain(input: string, blocklist: Iterable<string>): boolean {
  return isBlockedHost(input, buildBlockedHostSet(blocklist));
}

/**
 * Parse input that may contain multiple domains (comma, space, or newline separated)
 * Returns array of valid, normalized domains
 */
export function parseMultipleDomains(input: string): string[] {
  // Split by comma, semicolon, newline, or multiple spaces
  const parts = input.split(/[,;\n]+|\s{2,}/);

  const domains: string[] = [];
  for (const part of parts) {
    const normalized = normalizeDomain(part);
    if (normalized && isValidDomain(part)) {
      domains.push(normalized);
    }
  }

  // Return unique domains
  return [...new Set(domains)];
}

/**
 * Get display name for a source from ChatSourceItem
 */
export function getSourceDisplayName(source: ChatSourceItem): string {
  if (Array.isArray(source.website_name)) {
    return source.website_name[0] ?? source.domain;
  }
  return source.website_name ?? source.domain;
}

/**
 * Get category/type for a source
 */
export function getSourceCategory(source: ChatSourceItem): string {
  const type = source.website_type[0];
  if (type) {
    // Clean up the type string for display
    return type.replace(/_/g, ' ');
  }
  return 'Other';
}

/**
 * Reliability tier derived from authoritativeness_score.
 * Used for grouping and sorting sources into meaningful categories.
 */
export type ReliabilityTier =
  | 'highly_reliable'
  | 'reliable'
  | 'somewhat_reliable'
  | 'unverified';

interface ReliabilityTierInfo {
  tier: ReliabilityTier;
  /** Sort priority (higher = more authoritative, sorted first) */
  priority: number;
}

const TIER_THRESHOLDS: { min: number; tier: ReliabilityTier; priority: number }[] = [
  { min: 90, tier: 'highly_reliable', priority: 4 },
  { min: 75, tier: 'reliable', priority: 3 },
  { min: 50, tier: 'somewhat_reliable', priority: 2 },
  { min: 0, tier: 'unverified', priority: 1 },
];

/**
 * Map an authoritativeness_score to a reliability tier.
 * - 90–100 → Highly reliable
 * - 75–89  → Reliable
 * - 50–74  → Somewhat reliable
 * - <50 or null → Unverified
 */
export function getReliabilityTier(
  score: number | null | undefined
): ReliabilityTierInfo {
  const s = score ?? 0;
  for (const t of TIER_THRESHOLDS) {
    if (s >= t.min) return { tier: t.tier, priority: t.priority };
  }
  return { tier: 'unverified', priority: 1 };
}

/**
 * Compare two sources by reliability tier, then alphabetically.
 * Returns a standard sort comparator result.
 */
export function compareByReliabilityTier(
  aScore: number | null | undefined,
  bScore: number | null | undefined
): number {
  const aTier = getReliabilityTier(aScore);
  const bTier = getReliabilityTier(bScore);
  return bTier.priority - aTier.priority;
}

/**
 * Get display label for a domain - looks up in API sources or predefined
 */
export function getDomainLabel(domain: string, apiSources?: ChatSourceItem[]): string {
  // Check API sources first
  if (apiSources) {
    const apiSource = apiSources.find((s) => s.domain === domain);
    if (apiSource) {
      return getSourceDisplayName(apiSource);
    }
  }

  // Check predefined sources
  const predefined = PREDEFINED_SOURCES.find((s) => s.domain === domain);
  if (predefined) {
    return predefined.label;
  }

  return domain;
}
